// Import default workflows
import whoIsGideonWorkflow from '../data/defaultWorkflows/whoIsGideon.json';
import whoIsJonahWorkflow from '../data/defaultWorkflows/whoIsJonah.json';
import whoIsMosesWorkflow from '../data/defaultWorkflows/whoIsMoses.json';
import storyOfDavidAndGoliathWorkflow from '../data/defaultWorkflows/storyOfDavidAndGoliath.json';

class DefaultWorkflowService {
  constructor() {
    // Map suggestion texts to their corresponding workflows
    this.workflowMap = new Map([
      ['Who is Gideon?', whoIsGideonWorkflow],
      ['Who is Jonah?', whoIsJonahWorkflow],
      ['Who is Moses?', whoIsMosesWorkflow],
      ['Story of David and Goliath', storyOfDavidAndGoliathWorkflow],
      // Add more default workflows here as needed
    ]);
  }

  /**
   * Get a default workflow by suggestion text
   * @param {string} suggestionText - The text of the suggestion link
   * @returns {Object|null} - The workflow data or null if not found
   */
  getWorkflow(suggestionText) {
    return this.workflowMap.get(suggestionText) || null;
  }

  /**
   * Check if a suggestion has a default workflow
   * @param {string} suggestionText - The text of the suggestion link
   * @returns {boolean} - True if workflow exists
   */
  hasWorkflow(suggestionText) {
    return this.workflowMap.has(suggestionText);
  }

  /**
   * Get all available default workflows
   * @returns {Array} - Array of {text, workflow} objects
   */
  getAllWorkflows() {
    return Array.from(this.workflowMap.entries()).map(([text, workflow]) => ({
      text,
      workflow
    }));
  }

  /**
   * Load a default workflow with proper formatting for the flow system
   * @param {string} suggestionText - The text of the suggestion link
   * @returns {Object|null} - Formatted workflow data or null
   */
  loadWorkflowForSuggestion(suggestionText) {
    const workflow = this.getWorkflow(suggestionText);

    if (!workflow) {
      return null;
    }

    // Console log for Jonah workflow debugging
    if (suggestionText === "Who is Jonah?") {
      console.group("🐋 JONAH WORKFLOW DEBUG");
      console.log("Raw workflow data:", JSON.stringify(workflow, null, 2));
      console.log("Nodes with positions:", workflow.nodes.map(node => ({
        id: node.id,
        position: node.position,
        question: node.data.question
      })));
      console.groupEnd();
    }

    // Ensure proper node data structure
    const formattedNodes = workflow.nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        // Ensure all required properties are present
        isExpanded: node.data.isExpanded ?? false,
        isFirstNode: node.data.isFirstNode ?? false,
      }
    }));

    const formattedWorkflow = {
      ...workflow,
      nodes: formattedNodes,
      metadata: {
        ...workflow.metadata,
        loadedAt: new Date().toISOString(),
        source: 'default_workflow'
      }
    };

    // Log formatted workflow for Jonah
    if (suggestionText === "Who is Jonah?") {
      console.group("🐋 JONAH FORMATTED WORKFLOW");
      console.log("Formatted workflow:", JSON.stringify(formattedWorkflow, null, 2));
      console.groupEnd();
    }

    return formattedWorkflow;
  }
}

// Export singleton instance
const defaultWorkflowService = new DefaultWorkflowService();
export default defaultWorkflowService;